import React, { useState } from 'react';
import { Product } from '../../types/product';
import { useQueryClient, useMutation } from 'react-query';
import { formatCurrency } from '../../utils/currency';

const ProductCard = ({ product, cartContents, showQuickAdd = false }: { product: Product, cartContents: any, showQuickAdd?: boolean }) => {
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [currentPrice, setCurrentPrice] = useState(product.price);
  const queryClient = useQueryClient();

  const addToCartMutation = useMutation({
    mutationFn: async (variantId: number) => {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate cart query to trigger refresh
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cartContents } }));
    }
  });

  const handleVariantChange = (variantId: number) => {
    setSelectedVariantId(variantId);
    const selectedVariant = product.variants.find(variant => variant.id === variantId);
    if (selectedVariant) {
      setCurrentPrice(selectedVariant.price);
    } else {
      setCurrentPrice(product.price);
    }
  };

  const handleAddToCart = () => {
    if (!selectedVariantId) return;
    addToCartMutation.mutate(selectedVariantId);
  };

  return (
    <div className="product-card">
      <figure className="product-card__media">
        <img src={product.media[0].preview_image.src} alt={product.title} />
      </figure>
      <div className="product-card__content">
        <h3 className="product-card__title">
          <a href={product.url}>{product.title}</a>
        </h3>
        <p className="product-card__price">{formatCurrency(currentPrice)}</p>
        {showQuickAdd && (
          <>
            <select 
              className="product-card__variant-select"
              value={selectedVariantId || ''}
              onChange={(e) => handleVariantChange(Number(e.target.value))}
              aria-label="Select product variant"
            >
              <option value="">Select variant</option>
              {product.variants.map(variant => (
                <option key={variant.id} value={variant.id} disabled={!variant.available}>
                  {variant.title}
                </option>
              ))}
            </select>
            <button 
              className="product-card__quick-add" 
              onClick={handleAddToCart}
              disabled={!selectedVariantId || addToCartMutation.isLoading}
            >
              {addToCartMutation.isLoading ? 'Adding...' : 'Add to Cart'}
            </button>
            {addToCartMutation.isError && (
              <p className="product-card__error" role="alert">
                Failed to add to cart. Please try again.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ProductCard;